import { useEffect, useState, useCallback, useRef } from "react";
import TopBar from "@/components/TopBar";
import MapView from "@/components/MapView";
import api, { extractError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useGeoTracking } from "@/hooks/useGeoTracking";
import useWebSocket from "@/hooks/useWebSocket";
import useDriverAlerts from "@/hooks/useDriverAlerts";
import PushEnableBanner from "@/components/PushEnableBanner";
import OrderChat from "@/components/OrderChat";
import MissedCallBanner from "@/components/MissedCallBanner";
import OrderOfferModal from "@/components/OrderOfferModal";
import { useVoice } from "@/contexts/VoiceCallContext";
import { maskPhone } from "@/utils/maskPhone";
import { CheckCircle, XCircle, Navigation, MapPin, Wallet, TrendingUp, Bike, Phone, User, PackageCheck, Zap, ZapOff, LocateFixed, AlertCircle, Radio, Volume2, VolumeX, PhoneCall, ArrowDownToLine } from "lucide-react";
import DriverCashoutModal from "@/components/DriverCashoutModal";

const DEFAULT_REST_LOC = { lat: 32.0665, lng: 12.5303 };

export default function DriverDashboard() {
  const { user } = useAuth();
  const voice = useVoice();
  const [tab, setTab] = useState("current"); // current | available | earnings
  // Auto-dispatch: latest offer pushed by the backend to this driver (nearest first).
  const [offer, setOffer] = useState(null);
  const [available, setAvailable] = useState([]);
  const [current, setCurrent] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [driverStatus, setDriverStatus] = useState("offline");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const alerts = useDriverAlerts();
  const knownAvailableIdsRef = useRef(new Set());
  const [showCashout, setShowCashout] = useState(false);

  // Real GPS: broadcast live location ONLY while the driver is online (متصل).
  // Once he toggles to "غير متصل" the watch is torn down and no more /driver/location
  // pings hit the server, so his marker disappears from the admin live-map.
  const geoEnabled = driverStatus === "available";
  const { coords: liveLoc, status: geoStatus, error: geoErr } = useGeoTracking({
    enabled: geoEnabled,
    minIntervalMs: 4000,
    onUpdate: async (c) => {
      try { await api.post("/driver/location", { lat: c.lat, lng: c.lng }); } catch { /* ignore stale offline pushes */ }
    },
  });

  const refresh = useCallback(async () => {
    try {
      const [c, a, e] = await Promise.all([
        api.get("/driver/current"),
        api.get("/driver/available-orders"),
        api.get("/driver/earnings"),
      ]);
      setCurrent(c.data);
      setAvailable(a.data || []);
      setEarnings(e.data);
    } catch (ex) { setErr(extractError(ex)); }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 20000); // fallback slow poll
    return () => clearInterval(t);
  }, [refresh]);

  // WebSocket: instant refresh on order events + capture auto-dispatch offers
  const { status: wsStatus } = useWebSocket({
    onMessage: (msg) => {
      if (msg.event === "order.offered") {
        setOffer(msg.data || null);
        return;
      }
      if (msg.event === "order.offer.cancelled") {
        setOffer((cur) => (cur && cur.order_id === msg.data?.order_id ? null : cur));
        return;
      }
      if (msg.event?.startsWith("order.")) refresh();
    },
  });

  const accept = async (id) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/driver/orders/${id}/accept`);
      await refresh();
      setTab("current");
    } catch (e) { setErr(extractError(e)); }
    setBusy(false);
  };

  const pickup = async () => {
    if (!current) return;
    setBusy(true);
    try { await api.post(`/driver/orders/${current.id}/pickup`); await refresh(); }
    catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };

  const deliver = async () => {
    if (!current) return;
    setBusy(true);
    try { await api.post(`/driver/orders/${current.id}/deliver`); await refresh(); }
    catch (e) { setErr(extractError(e)); } finally { setBusy(false); }
  };

  const toggleStatus = async () => {
    const next = driverStatus === "available" ? "offline" : "available";
    setDriverStatus(next);
    try { await api.post(`/driver/status?status=${next}`); } catch {}
  };

  return (
    <div className="min-h-screen bg-[var(--background)]">
      <TopBar />
      <PushEnableBanner/>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">

        {/* Driver header */}
        <div className="bg-[var(--text-primary)] text-white rounded-3xl p-6 mb-6 relative overflow-hidden">
          <div className="grain-overlay"/>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-primary-brand flex items-center justify-center text-2xl font-black" style={{fontFamily:'Cairo'}}>
                {user?.name?.[0]}
              </div>
              <div>
                <div className="text-xs opacity-70">مرحباً</div>
                <div className="font-black text-xl" style={{fontFamily:'Cairo'}}>{user?.name}</div>
                <div className="text-xs opacity-70 flex items-center gap-1 mt-0.5"><Bike size={12}/> طلبية مان</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { alerts.toggle(); if (!alerts.enabled) alerts.testAlert(); }}
                className={`w-11 h-11 rounded-xl flex items-center justify-center transition ${alerts.enabled ? "bg-white/15 hover:bg-white/25" : "bg-white/5 opacity-60"}`}
                title={alerts.enabled ? "التنبيهات مفعّلة (اضغط للإسكات)" : "التنبيهات صامتة"}
                data-testid="alerts-toggle">
                {alerts.enabled ? <Volume2 size={18}/> : <VolumeX size={18}/>}
              </button>
              <button onClick={toggleStatus}
                className={`px-4 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2 ${driverStatus === "available" ? "bg-green-500" : "bg-red-500"}`}
                data-testid="toggle-status">
                {driverStatus === "available" ? <><Zap size={16}/> متصل</> : <><ZapOff size={16}/> غير متصل</>}
              </button>
            </div>
          </div>
          {wsStatus === "open" && driverStatus === "available" && (
            <div className="relative z-10 flex items-center gap-1.5 mt-3 text-xs opacity-80" data-testid="ws-live">
              <Radio size={11} className="animate-pulse-dot"/> بث لحظي مفعل
            </div>
          )}
          {wsStatus === "open" && driverStatus !== "available" && (
            <div className="relative z-10 flex items-center gap-1.5 mt-3 text-xs opacity-60" data-testid="ws-live-off">
              <Radio size={11}/> البث اللحظي متوقف — فعّل «متصل» ليصلك الطلبات
            </div>
          )}

          {earnings && (
            <>
            <div className="relative z-10 grid grid-cols-3 gap-3 mt-6">
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <div className="text-xs opacity-70">رصيد اليوم</div>
                <div className="font-black text-lg" style={{fontFamily:'Cairo'}} data-testid="today-payout">{earnings.today_earnings} د.ل</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <div className="text-xs opacity-70">توصيلات اليوم</div>
                <div className="font-black text-lg" style={{fontFamily:'Cairo'}}>{earnings.today_orders}</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <div className="text-xs opacity-70">إجمالي المحفظة</div>
                <div className="font-black text-lg" style={{fontFamily:'Cairo'}} data-testid="total-payout">{earnings.total_earnings} د.ل</div>
              </div>
            </div>
            <div className="relative z-10 mt-3 text-[10px] opacity-70 text-center" data-testid="wallet-policy">
              💰 المحفظة تُحسب من رسوم التوصيل فقط (100% للسائق)
            </div>
            </>
          )}
        </div>

        {/* GPS Status */}
        {geoEnabled && (
          <div
            className={`mb-4 px-4 py-3 rounded-2xl flex items-center gap-3 border ${
              geoStatus === "granted" ? "bg-green-50 border-green-200 text-green-800"
              : geoStatus === "requesting" ? "bg-blue-50 border-blue-200 text-blue-800"
              : "bg-yellow-50 border-yellow-200 text-yellow-800"
            }`}
            data-testid="gps-status">
            {geoStatus === "granted" ? (
              <>
                <LocateFixed size={18} className="animate-pulse-dot"/>
                <div className="flex-1">
                  <div className="text-sm font-bold">GPS متصل</div>
                  <div className="text-xs opacity-80">
                    {liveLoc ? `الموقع: ${liveLoc.lat.toFixed(5)}, ${liveLoc.lng.toFixed(5)}` : "بانتظار الإحداثيات..."}
                    {liveLoc?.accuracy && ` · دقة ±${Math.round(liveLoc.accuracy)}م`}
                  </div>
                </div>
              </>
            ) : geoStatus === "requesting" ? (
              <>
                <LocateFixed size={18}/>
                <div className="text-sm font-bold">جاري الحصول على موقعك...</div>
              </>
            ) : (
              <>
                <AlertCircle size={18}/>
                <div className="flex-1">
                  <div className="text-sm font-bold">تعذّر الوصول للموقع</div>
                  <div className="text-xs opacity-80">{geoErr || "يرجى السماح بإذن الموقع في المتصفح لبدء التوصيل"}</div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 bg-white p-1.5 rounded-2xl talabya-shadow-soft mb-4 sticky top-[64px] z-20">
          <button onClick={() => setTab("current")}
            className={`flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 transition ${tab === "current" ? "bg-primary-brand text-white" : "text-[var(--text-secondary)]"}`}
            data-testid="tab-current">
            <Navigation size={14}/> الرحلة الحالية {current && "•"}
          </button>
          <button onClick={() => setTab("available")}
            className={`flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 transition ${tab === "available" ? "bg-primary-brand text-white" : "text-[var(--text-secondary)]"}`}
            data-testid="tab-available">
            <PackageCheck size={14}/> طلبات متاحة {available.length > 0 && <span className="bg-white/30 px-1.5 rounded">{available.length}</span>}
          </button>
          <button onClick={() => setTab("earnings")}
            className={`flex-1 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-1.5 transition ${tab === "earnings" ? "bg-primary-brand text-white" : "text-[var(--text-secondary)]"}`}
            data-testid="tab-earnings">
            <Wallet size={14}/> المحفظة
          </button>
        </div>

        {err && <div className="bg-red-50 text-red-600 px-4 py-2 rounded-xl text-sm mb-3">{err}</div>}

        {/* Current Trip */}
        {tab === "current" && (
          !current ? (
            <div className="bg-white rounded-3xl p-12 text-center talabya-shadow-soft" data-testid="no-current">
              <div className="w-20 h-20 rounded-full bg-[var(--surface-alt)] flex items-center justify-center mx-auto mb-4">
                <Bike size={32} className="text-[var(--text-secondary)]"/>
              </div>
              <h2 className="text-xl font-bold mb-2" style={{fontFamily:'Cairo'}}>لا يوجد رحلة حالية</h2>
              <p className="text-[var(--text-secondary)] text-sm mb-4">تحقق من الطلبات المتاحة لبدء رحلة جديدة</p>
              <button onClick={() => setTab("available")} className="px-5 py-2.5 rounded-xl bg-primary-brand text-white font-bold" data-testid="go-available">
                عرض الطلبات المتاحة
              </button>
            </div>
          ) : (
            <div>
              <MissedCallBanner orderId={current.id}/>
              <div className="rounded-3xl overflow-hidden talabya-shadow-soft border border-[var(--border)] mb-4">
                <div className="h-72 sm:h-80">
                  <MapView
                    restaurant={current.restaurant ? [current.restaurant.lat, current.restaurant.lng] : [DEFAULT_REST_LOC.lat, DEFAULT_REST_LOC.lng]}
                    driver={liveLoc ? [liveLoc.lat, liveLoc.lng] : null}
                    customer={current.address ? [current.address.lat, current.address.lng] : null}
                  />
                </div>
              </div>

              <div className="bg-white rounded-3xl p-5 talabya-shadow-soft mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-2 h-2 rounded-full animate-pulse-dot ${current.status === "assigned" ? "bg-blue-500" : current.status === "picked_up" ? "bg-primary-brand" : "bg-amber-500"}`}/>
                  <div className="font-bold text-lg" style={{fontFamily:'Cairo'}}>
                    {current.status === "pending" ? "بانتظار تأكيد المطعم" :
                     current.status === "preparing" ? "المطعم يُحضّر الطلب" :
                     current.status === "assigned" ? "توجه إلى المطعم" : "توجه إلى الزبون"}
                  </div>
                </div>

                {(current.status === "pending" || current.status === "preparing") ? (
                  <div className="flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200">
                    <MapPin className="text-amber-600 mt-0.5"/>
                    <div className="flex-1">
                      <div className="font-bold">{current.restaurant?.name || "مطعم طلبية"}</div>
                      <div className="text-sm text-[var(--text-secondary)]">
                        {current.restaurant?.address || ""}
                      </div>
                      <div className="text-xs text-amber-700 mt-1 font-bold">
                        {current.status === "pending" ? "الطلب محجوز لك — سنُخطرك فور جاهزيته" : "سيتم إخطارك فور اكتمال التحضير"}
                      </div>
                    </div>
                  </div>
                ) : current.status === "assigned" ? (
                  <div className="flex items-start gap-3 p-4 rounded-2xl bg-primary-light">
                    <MapPin className="text-primary-brand mt-0.5"/>
                    <div className="flex-1">
                      <div className="font-bold">{current.restaurant?.name || "مطعم طلبية"}</div>
                      <div className="text-sm text-[var(--text-secondary)]">
                        {current.restaurant?.address || "شارع السوق الرئيسي، يفرن"}
                      </div>
                      {current.restaurant?.phone && (
                        <a href={`tel:${current.restaurant.phone}`} className="text-xs text-primary-brand font-bold mt-1 inline-flex items-center gap-1">
                          <Phone size={11}/> {current.restaurant.phone}
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3 p-4 rounded-2xl bg-primary-light">
                    <MapPin className="text-primary-brand mt-0.5"/>
                    <div className="flex-1">
                      <div className="font-bold">{current.customer_name}</div>
                      <div className="text-sm text-[var(--text-secondary)]">
                        {current.address?.street}، {current.address?.city}
                      </div>
                      {current.address?.notes && (
                        <div className="text-xs italic text-[var(--text-secondary)] mt-1">"{current.address.notes}"</div>
                      )}
                    </div>
                    {current.customer_phone && (
                      <div className="flex flex-col items-center gap-1 shrink-0" data-testid="customer-call-cluster">
                        <a
                          href={`tel:${current.customer_phone}`}
                          className="w-11 h-11 rounded-full bg-green-600 text-white flex items-center justify-center talabya-shadow-float hover:bg-green-700"
                          aria-label="اتصال هاتفي مباشر بالزبون"
                          data-testid="direct-call-customer"
                          title="اتصال هاتفي مباشر بالزبون"
                        >
                          <Phone size={17}/>
                        </a>
                        <span className="text-[10px] font-black text-green-700 tracking-tight" data-testid="customer-phone-masked">
                          {maskPhone(current.customer_phone)}
                        </span>
                        <button
                          onClick={() => voice.startOutgoing(current.id, { name: current.customer_name })}
                          disabled={voice.state !== "idle" && voice.state !== "ended" && voice.state !== "error"}
                          className="mt-0.5 px-2 py-1 rounded-full bg-primary-brand text-white flex items-center justify-center gap-1 text-[10px] font-bold talabya-shadow-soft disabled:opacity-50 hover:bg-blue-800"
                          aria-label="اتصال صوتي داخل التطبيق (بدون رصيد)"
                          data-testid="voice-call-customer"
                          title="اتصال داخل التطبيق (بدون رصيد)"
                        >
                          <PhoneCall size={12}/> داخل التطبيق
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-3 p-3 rounded-xl bg-[var(--surface-alt)]">
                  <div className="text-xs text-[var(--text-secondary)] mb-1">رقم الطلب</div>
                  <div className="font-black" style={{fontFamily:'Cairo'}}>{current.code}</div>
                  <div className="text-xs text-[var(--text-secondary)] mt-2 mb-1">الأصناف ({current.items.length})</div>
                  <div className="text-sm">{current.items.map(i => `${i.name} ×${i.quantity}`).join("، ")}</div>
                  <div className="text-xs text-[var(--text-secondary)] mt-2 mb-1">إجمالي الطلب (دفع نقداً)</div>
                  <div className="font-black text-primary-brand" style={{fontFamily:'Cairo'}}>{current.total.toFixed(2)} د.ل</div>
                </div>

                {current.status === "pending" || current.status === "preparing" ? (
                  <div className="w-full mt-4 py-4 rounded-2xl bg-amber-100 text-amber-700 font-bold text-center" data-testid="waiting-restaurant">
                    ⏳ بانتظار المطعم لتأكيد جاهزية الطلب
                  </div>
                ) : current.status === "assigned" ? (
                  <button onClick={pickup} disabled={busy} className="w-full mt-4 py-4 rounded-2xl bg-primary-brand text-white font-bold talabya-shadow-float disabled:opacity-60" data-testid="btn-pickup">
                    استلمت الطلب من المطعم
                  </button>
                ) : (
                  <button onClick={deliver} disabled={busy} className="w-full mt-4 py-4 rounded-2xl bg-green-600 text-white font-bold talabya-shadow-float disabled:opacity-60" data-testid="btn-deliver">
                    <CheckCircle size={18} className="inline ml-2"/> تم التسليم
                  </button>
                )}
              </div>
              {/* Chat with customer */}
              {(current.status === "assigned" || current.status === "picked_up") && (
                <div className="mt-4">
                  <OrderChat orderId={current.id} driverPhone={current.customer_phone} compact/>
                </div>
              )}
            </div>
          )
        )}

        {/* Available Orders */}
        {tab === "available" && (
          <div className="flex flex-col gap-3" data-testid="available-list">
            {available.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center talabya-shadow-soft">
                <PackageCheck size={40} className="mx-auto text-[var(--text-secondary)] mb-3"/>
                <div className="text-lg font-bold" style={{fontFamily:'Cairo'}}>لا توجد طلبات متاحة الآن</div>
                <div className="text-sm text-[var(--text-secondary)] mt-1">سنُخطرك فور وصول طلب جديد</div>
              </div>
            ) : available.map((o) => {
              const statusInfo = {
                pending: { text: "طلب جديد · بانتظار تأكيد المطعم", color: "text-amber-600", bg: "border-amber-300" },
                preparing: { text: "قيد التحضير في المطعم", color: "text-blue-600", bg: "border-blue-300" },
                ready: { text: "جاهز للاستلام الآن", color: "text-green-600", bg: "border-green-400" },
              }[o.status] || { text: "طلب جديد", color: "text-primary-brand", bg: "border-primary-brand/20" };
              return (
              <div key={o.id} className={`bg-white rounded-3xl p-5 talabya-shadow-float border-2 ${statusInfo.bg} animate-fade-up`} data-testid={`avail-${o.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className={`text-xs font-bold uppercase tracking-widest ${statusInfo.color}`} data-testid={`avail-status-${o.id}`}>{statusInfo.text}</div>
                    <div className="font-black text-lg" style={{fontFamily:'Cairo'}}>{o.code}</div>
                  </div>
                  <div className="text-left">
                    <div className="text-xs text-[var(--text-secondary)]">إجمالي</div>
                    <div className="font-black text-primary-brand text-xl" style={{fontFamily:'Cairo'}}>{o.total.toFixed(2)} د.ل</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 rounded-xl bg-[var(--surface-alt)]">
                    <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1"><MapPin size={12}/> استلام</div>
                    <div className="font-bold text-sm mt-0.5">{o.branch?.name || "مطعم طلبية"}</div>
                    {o.branch?.address && <div className="text-[10px] text-[var(--text-secondary)] mt-0.5 truncate">{o.branch.address}</div>}
                  </div>
                  <div className="p-3 rounded-xl bg-primary-light">
                    <div className="text-xs text-primary-brand flex items-center gap-1"><User size={12}/> توصيل</div>
                    <div className="font-bold text-sm mt-0.5">{o.address?.street}</div>
                  </div>
                </div>
                <div className="text-xs text-[var(--text-secondary)] mb-3">
                  {o.items.length} صنف · {o.customer_name}
                </div>
                <div className="flex gap-2">
                  <button disabled={busy} className="flex-1 py-3 rounded-xl bg-[var(--surface-alt)] text-[var(--text-primary)] font-bold hover:bg-red-50 hover:text-red-600 transition flex items-center justify-center gap-2" data-testid={`decline-${o.id}`}>
                    <XCircle size={16}/> رفض
                  </button>
                  <button onClick={() => accept(o.id)} disabled={busy} className="flex-[2] py-3 rounded-xl bg-primary-brand text-white font-bold talabya-shadow-float flex items-center justify-center gap-2 disabled:opacity-60" data-testid={`accept-${o.id}`}>
                    <CheckCircle size={16}/> {o.status === "ready" ? "قبول الطلب" : "حجز الطلب"}
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {/* Earnings */}
        {tab === "earnings" && earnings && (
          <div>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-white rounded-2xl p-5 talabya-shadow-soft">
                <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1"><Wallet size={12}/> اليوم</div>
                <div className="font-black text-2xl text-primary-brand mt-2" style={{fontFamily:'Cairo'}}>{earnings.today_earnings} د.ل</div>
                <div className="text-xs text-[var(--text-secondary)] mt-1">{earnings.today_orders} توصيلة</div>
              </div>
              <div className="bg-white rounded-2xl p-5 talabya-shadow-soft">
                <div className="text-xs text-[var(--text-secondary)] flex items-center gap-1"><TrendingUp size={12}/> الإجمالي</div>
                <div className="font-black text-2xl mt-2" style={{fontFamily:'Cairo'}}>{earnings.total_earnings} د.ل</div>
                <div className="text-xs text-[var(--text-secondary)] mt-1">{earnings.total_orders} توصيلة</div>
              </div>
            </div>
            <button
              onClick={() => setShowCashout(true)}
              className="w-full mb-4 py-3.5 rounded-2xl bg-gradient-to-l from-primary-brand to-[#1E3AB0] text-white font-black flex items-center justify-center gap-2 talabya-shadow-soft active:scale-[0.98] transition"
              data-testid="driver-cashout-btn"
            >
              <ArrowDownToLine size={18}/> طلب سحب
            </button>
            <div className="bg-white rounded-3xl p-5 talabya-shadow-soft">
              <div className="font-bold mb-3" style={{fontFamily:'Cairo'}}>آخر التوصيلات</div>
              {earnings.recent.length === 0 ? (
                <div className="text-center text-sm text-[var(--text-secondary)] py-6">لا توجد توصيلات بعد</div>
              ) : earnings.recent.map((r) => (
                <div key={r.id} className="flex justify-between py-2 border-b border-[var(--border)]/50 last:border-0">
                  <div>
                    <div className="font-bold text-sm" style={{fontFamily:'Cairo'}}>{r.code}</div>
                    <div className="text-xs text-[var(--text-secondary)]">{new Date(r.at).toLocaleString("ar-LY", { dateStyle: "short", timeStyle: "short" })}</div>
                  </div>
                  <div className="text-primary-brand font-black" style={{fontFamily:'Cairo'}}>+{r.payout} د.ل</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Auto-dispatch offer modal — pushed by backend to nearest driver */}
      {offer && (
        <OrderOfferModal
          offer={offer}
          onAccepted={() => { setOffer(null); refresh(); setTab("current"); }}
          onDismiss={() => setOffer(null)}
        />
      )}
      {showCashout && <DriverCashoutModal onClose={() => { setShowCashout(false); refresh(); }}/>}
    </div>
  );
}
